/** Offer window, plan line-up and form options for the Performance Lab festive page. */
import type { PerformanceLabPlan } from '../buy/india/types';
import { getActivePlanOffer } from '../buy/india/planOffer';
import {
  LAB_PRICE_CUT_DISCOUNT_CODE,
  LAB_PRICE_CUT_ENDS,
  LAB_PRICE_CUT_STARTS,
} from '../buy/india/priceCut';

/** 23 Oct – 10 Nov (IST). Same window as the Lab price cut. */
export const FESTIVE_OFFER_STARTS = LAB_PRICE_CUT_STARTS;
export const FESTIVE_OFFER_ENDS = LAB_PRICE_CUT_ENDS;
/** Nbsp-glued so the range wraps as a unit (never orphans “10”). */
export const FESTIVE_OFFER_DATES_LABEL = 'Oct\u00A023\u00A0–\u00A0Nov\u00A010';

export type FestivePanelKey = 'diagnostics' | 'membership' | 'recovery' | 'iv';

export type FestiveSubPlanDef = {
  id: string;
  nameKey: string;
  validityKey: string;
  giftLineKey: string;
  /** India catalogue plan. Its active offer is the price, including the price cut. */
  catalogueId?: string;
  /** Units of that variant. An IV pack is 4 of the single-session variant. */
  quantity?: number;
  /** Gift-only Shopify variant. There is no staging id; both environments use this. */
  shopifyVariantId?: string;
  price?: number;
  strikePrice?: number;
  perSessionPrice?: number;
  /** Carries the "Recommended" tag at checkout. */
  recommended?: boolean;
};

export type FestiveCardDef = {
  id: string;
  nameKey: string;
  /** Omitted when the card is one catalogue plan and should use that plan's description. */
  descriptionKey?: string;
  specKey: string;
  helperKey: string;
  categoryKey: string;
  chipKeys?: readonly string[];
  includeKeys?: readonly string[];
  subPlans: readonly FestiveSubPlanDef[];
};

export type FestivePanelDef = {
  key: FestivePanelKey;
  headingKey: string;
  bodyKey: string;
  look: 'longevity' | 'recovery';
  tone: 'sky' | 'stone' | 'lavender' | 'clay';
  columns: 2 | 3;
  cards: readonly FestiveCardDef[];
};

const sub = (
  id: string,
  extra: Omit<
    FestiveSubPlanDef,
    'id' | 'nameKey' | 'validityKey' | 'giftLineKey'
  > & {
    nameKey?: string;
    validityKey?: string;
    giftLineKey?: string;
  } = {},
): FestiveSubPlanDef => ({
  id,
  nameKey: extra.nameKey ?? `festive.lineup.subs.${id}.name`,
  validityKey: extra.validityKey ?? `festive.lineup.subs.${id}.validity`,
  giftLineKey: extra.giftLineKey ?? `festive.lineup.subs.${id}.giftLine`,
  ...extra,
});

const card = (
  id: string,
  panelBits: Omit<
    FestiveCardDef,
    'id' | 'nameKey' | 'specKey' | 'helperKey' | 'categoryKey'
  > & {
    categoryKey: string;
    descriptionKey?: string;
    specKey?: string;
    helperKey?: string;
  },
): FestiveCardDef => ({
  id,
  nameKey: `festive.lineup.cards.${id}.name`,
  descriptionKey:
    panelBits.descriptionKey ?? `festive.lineup.cards.${id}.description`,
  specKey: panelBits.specKey ?? `festive.lineup.cards.${id}.spec`,
  helperKey: panelBits.helperKey ?? `festive.lineup.cards.${id}.helper`,
  categoryKey: panelBits.categoryKey,
  chipKeys: panelBits.chipKeys,
  includeKeys: panelBits.includeKeys,
  subPlans: panelBits.subPlans,
});

const catalogueSub = (
  id: string,
  catalogueId: string,
  quantity = 1,
): FestiveSubPlanDef => sub(id, { catalogueId, quantity });

/**
 * Gift-page lineup. Shared plans resolve through the India catalogue.
 * New SKUs stay in this list and are not added to the buy page.
 */
export const FESTIVE_PANELS: readonly FestivePanelDef[] = [
  {
    key: 'diagnostics',
    headingKey: 'festive.versions.v2.panels.fullPicture',
    bodyKey: 'festive.versions.v2.panelBody.fullPicture',
    look: 'longevity',
    tone: 'sky',
    columns: 2,
    cards: [
      card('diagnostics-basic', {
        categoryKey: 'festive.versions.plans.categoryLabel.longevity',
        descriptionKey: '',
        subPlans: [catalogueSub('diagnostics-basic', 'diagnostics-basic')],
      }),
      card('diagnostics-pro', {
        categoryKey: 'festive.versions.plans.categoryLabel.longevity',
        descriptionKey: '',
        subPlans: [catalogueSub('diagnostics-pro', 'diagnostics-pro')],
      }),
      card('diagnostics-elite', {
        categoryKey: 'festive.versions.plans.categoryLabel.longevity',
        descriptionKey: '',
        subPlans: [catalogueSub('diagnostics-elite', 'diagnostics-elite')],
      }),
    ],
  },
  {
    key: 'membership',
    headingKey: 'festive.lineup.panels.membership.heading',
    bodyKey: 'festive.lineup.panels.membership.body',
    look: 'recovery',
    tone: 'stone',
    columns: 2,
    cards: [
      card('recovery-membership-monthly', {
        categoryKey: 'festive.versions.plans.categoryLabel.recovery',
        includeKeys: [
          'festive.lineup.cards.recovery-membership-monthly.includes.0',
          'festive.lineup.cards.recovery-membership-monthly.includes.1',
          'festive.lineup.cards.recovery-membership-monthly.includes.2',
        ],
        subPlans: [
          // List/offer from Shopify audit; GIFT_ONLY_PRICE_CUT applies EXINPL15 in window.
          sub('membership-monthly', {
            shopifyVariantId: '46436534386758',
            price: 22999,
          }),
        ],
      }),
      card('recovery-membership-annual', {
        categoryKey: 'festive.versions.plans.categoryLabel.recovery',
        includeKeys: [
          'festive.lineup.cards.recovery-membership-annual.includes.0',
          'festive.lineup.cards.recovery-membership-annual.includes.1',
          'festive.lineup.cards.recovery-membership-annual.includes.2',
        ],
        subPlans: [
          sub('membership-annual', {
            shopifyVariantId: '46436534648902',
            price: 199999,
            // Structural pack saving vs 12× monthly list — not the festive 15%.
          }),
        ],
      }),
    ],
  },
  {
    key: 'recovery',
    headingKey: 'festive.lineup.panels.recovery.heading',
    bodyKey: 'festive.lineup.panels.recovery.body',
    look: 'longevity',
    tone: 'sky',
    columns: 2,
    cards: [
      // Feedback order: session packs → sports massage → recovery passes.
      card('cryotherapy', {
        categoryKey: 'festive.versions.plans.categoryLabel.recovery',
        chipKeys: [
          'festive.lineup.cards.cryotherapy.chips.0',
          'festive.lineup.cards.cryotherapy.chips.1',
          'festive.lineup.cards.cryotherapy.chips.2',
        ],
        subPlans: [
          catalogueSub('cryo-1', 'pl-standalone-cryo'),
          sub('cryo-10', {
            recommended: true,
            shopifyVariantId: '46436535009350',
            price: 39990,
            perSessionPrice: 3999,
          }),
          sub('cryo-20', {
            shopifyVariantId: '46436535074886',
            price: 69980,
            perSessionPrice: 3499,
          }),
        ],
      }),
      card('hbot', {
        categoryKey: 'festive.versions.plans.categoryLabel.recovery',
        chipKeys: [
          'festive.lineup.cards.hbot.chips.0',
          'festive.lineup.cards.hbot.chips.1',
          'festive.lineup.cards.hbot.chips.2',
        ],
        subPlans: [
          catalogueSub('hbot-1', 'pl-standalone-hbot'),
          sub('hbot-10', {
            recommended: true,
            shopifyVariantId: '46436534812742',
            price: 59990,
            perSessionPrice: 5999,
          }),
          sub('hbot-20', {
            shopifyVariantId: '46436534943814',
            price: 99980,
            perSessionPrice: 4999,
          }),
        ],
      }),
      card('sports-massage', {
        categoryKey: 'festive.versions.plans.categoryLabel.recovery',
        chipKeys: [
          'festive.lineup.cards.sports-massage.chips.0',
          'festive.lineup.cards.sports-massage.chips.1',
          'festive.lineup.cards.sports-massage.chips.2',
        ],
        subPlans: [
          catalogueSub('massage-male-1', 'pl-standalone-massage-male'),
          catalogueSub('massage-female-1', 'pl-standalone-massage-female'),
          sub('massage-male-month', {
            shopifyVariantId: '46456015683654',
            price: 11897,
          }),
          sub('massage-female-month', {
            shopifyVariantId: '46456016240710',
            price: 11897,
          }),
          sub('massage-male-3-month', {
            shopifyVariantId: '46456016207942',
            price: 44787,
          }),
          sub('massage-female-3-month', {
            shopifyVariantId: '46456016273478',
            price: 44787,
          }),
        ],
      }),
      card('recovery-passes', {
        categoryKey: 'festive.versions.plans.categoryLabel.recovery',
        includeKeys: [
          'festive.lineup.includes.powerplate',
          'festive.lineup.includes.hotCold',
          'festive.lineup.includes.compression',
          'festive.lineup.includes.decompression',
          'festive.lineup.includes.massageGuns',
          'festive.lineup.includes.coldPlunge',
          'festive.lineup.includes.sauna',
        ],
        subPlans: [
          catalogueSub('pass-day', 'recovery-day-pass'),
          {
            ...catalogueSub('pass-month', 'recovery-month-pass'),
            recommended: true,
          },
          catalogueSub('pass-3-month', 'recovery-3-month-pass'),
        ],
      }),
    ],
  },
  {
    key: 'iv',
    headingKey: 'festive.lineup.panels.iv.heading',
    bodyKey: 'festive.lineup.panels.iv.body',
    look: 'recovery',
    tone: 'stone',
    columns: 2,
    cards: [
      card('iv-hydromax', {
        categoryKey: 'festive.lineup.category.iv',
        subPlans: [
          catalogueSub('hydromax-1', 'iv-hydromax', 1),
          // Shopify session-pack SKU (4 sessions, 30 days), not 4 × the single drip.
          sub('hydromax-4', {
            shopifyVariantId: '50687281954886',
            price: 33660,
            perSessionPrice: 8415,
          }),
        ],
      }),
      card('iv-vitaglow', {
        categoryKey: 'festive.lineup.category.iv',
        subPlans: [
          catalogueSub('vitaglow-1', 'iv-vitaglow', 1),
          // Shopify session-pack SKU (4 sessions, 30 days), not 4 × the single drip.
          sub('vitaglow-4', {
            shopifyVariantId: '50687282380870',
            price: 67660,
            perSessionPrice: 16915,
          }),
        ],
      }),
      card('iv-megaboost', {
        categoryKey: 'festive.lineup.category.iv',
        subPlans: [
          catalogueSub('megaboost-1', 'iv-megaboost', 1),
          // Shopify session-pack SKU (4 sessions, 30 days), not 4 × the single drip.
          sub('megaboost-4', {
            shopifyVariantId: '50687282184262',
            price: 67660,
            perSessionPrice: 16915,
          }),
        ],
      }),
      card('iv-nad-125', {
        categoryKey: 'festive.lineup.category.iv',
        subPlans: [
          catalogueSub('nad-1', 'iv-nad-125', 1),
          // Shopify session-pack SKU (4 sessions, 30 days), not 4 × the single drip.
          sub('nad-4', {
            shopifyVariantId: '50687282577478',
            price: 101660,
            perSessionPrice: 25415,
          }),
        ],
      }),
    ],
  },
];

export const FESTIVE_CARDS: readonly FestiveCardDef[] = FESTIVE_PANELS.flatMap(
  (panel) => panel.cards,
);

export const festiveCheckoutPath = (cardId: string, step?: string) => {
  const path = `/performance-lab/gift/checkout/${cardId}`;
  return step ? `${path}?step=${step}` : path;
};

export type FestivePricing = {
  price: number;
  strikePrice?: number;
  savePercent?: number;
  discountCode?: string;
};

/**
 * Same price the buy page shows. Affiliate / referral codes win over EXINPL15
 * (codes do not stack); other standing offers without a code still take priority.
 */
export const pricePlanLikeBuyPage = (
  plan: PerformanceLabPlan,
  affiliate: { percentage: number; discountCode: string },
): FestivePricing => {
  const offer = getActivePlanOffer(plan);
  const affiliateWins =
    affiliate.percentage > 0 &&
    Boolean(affiliate.discountCode) &&
    offer?.discountCode === LAB_PRICE_CUT_DISCOUNT_CODE;

  if (affiliateWins && plan.price > 0) {
    return {
      price: Math.round(plan.price * (1 - affiliate.percentage / 100)),
      strikePrice: plan.price,
      savePercent: affiliate.percentage,
      discountCode: affiliate.discountCode || undefined,
    };
  }

  if (offer && plan.price > 0) {
    const strike = offer.strikePrice ?? plan.price;
    const savePercent = Math.max(
      0,
      Math.round((1 - offer.offerPrice / strike) * 100),
    );
    return {
      price: offer.offerPrice,
      strikePrice: savePercent > 0 ? strike : undefined,
      savePercent: savePercent > 0 ? savePercent : undefined,
      discountCode: offer.discountCode,
    };
  }
  if (affiliate.percentage > 0 && plan.price > 0) {
    return {
      price: Math.round(plan.price * (1 - affiliate.percentage / 100)),
      strikePrice: plan.price,
      savePercent: affiliate.percentage,
      discountCode: affiliate.discountCode || undefined,
    };
  }
  return { price: plan.price };
};

/** "Who is this for?" options on the gift form; values go to the callback API. */
export const FESTIVE_GIFT_FOR_OPTIONS = [
  { value: 'parent', labelKey: 'festive.callbackForm.giftForOptions.parent' },
  { value: 'partner', labelKey: 'festive.callbackForm.giftForOptions.partner' },
  { value: 'myself', labelKey: 'festive.callbackForm.giftForOptions.myself' },
  { value: 'team', labelKey: 'festive.callbackForm.giftForOptions.team' },
  {
    value: 'someone_else',
    labelKey: 'festive.callbackForm.giftForOptions.someoneElse',
  },
] as const;

export const FESTIVE_PLANS_ANCHOR_ID = 'festive-plans';

export const festivePlanGroupAnchorId = (key: string) =>
  `${FESTIVE_PLANS_ANCHOR_ID}-${key}`;
